'use client';

import { Link } from '@components/link';
import { classNames } from '@lib/classNames/classNames';
import NextLink from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Typography } from '../../typography';
import { getNavigationLinks } from '../getNavigationLinks';
import { FooterProps, LinkEntity, NavigationLinks } from '../types';
import { MaskedPhoneBlock } from './MaskedPhoneBlock';

import IconChevron from '@/assets/icons/iconChevron.svg';
import IconLink from '@/assets/icons/iconLink.svg';
import IconLogo from '@/assets/icons/iconLogo.svg';
import IconLogoEng from '@/assets/icons/iconLogoEng.svg';
import IconMail from '@/assets/icons/iconMail.svg';
import IconPhone from '@/assets/icons/iconPhone.svg';
import IconTelegram from '@/assets/icons/iconTelegram.svg';
import IconVC from '@/assets/icons/iconVC.svg';
import { LOCALES, SCREEN_SM } from '@/shared/constants/common';
import { HOTLINE_PHONE } from '@/shared/constants/hotlinePhone';
import { useFeatureFlags } from '@/shared/context';
import { useGetClientHost, useSavedUtmParams, useUtmProduct } from '@/shared/hooks';
import {
  getDomainKey,
  isKzDomain,
  isRuDomain,
} from '@/shared/utils/getDomainInfo';
import { applyUtmParamsToHref } from '@/shared/utils/utmLinks';

export const Footer = ({ translations, locale }: FooterProps) => {
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>();
  const [isFirefoxOrSafari, setIsFirefoxOrSafari] = useState<boolean>(false);
  const utmParams = useSavedUtmParams();
  const utmProduct = useUtmProduct();
  const { isFeatureEnabled } = useFeatureFlags();
  const clientHost = useGetClientHost();
  const domain = getDomainKey(clientHost);

  const isRuHost = isRuDomain(clientHost);
  const isKzHost = isKzDomain(clientHost);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= SCREEN_SM);
    };

    setIsFirefoxOrSafari(
      navigator.userAgent.includes('Firefox') ||
        navigator.userAgent.includes('Safari'),
    );

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const openGroup = (groupName: string) => {
    setOpenGroups((openGroups) =>
      openGroups.includes(groupName)
        ? openGroups.filter((openedMenu) => openedMenu !== groupName)
        : [...openGroups, groupName],
    );
  };

  const getResolvedLink = useCallback(
    (href?: string) => {
      return href
        ? applyUtmParamsToHref(translations[href] || href, utmParams, utmProduct)
        : '';
    },
    [translations, utmParams, utmProduct],
  );

  const renderLinkByType = useCallback(
    (link: LinkEntity) => {
      const linkElements = [];

      /** Для подписок создадим отдельный блок */
      if (link.entityType === 'subscribe') {
        linkElements.push(
          <div className="flex gap-3" key="subscribe">
            <NextLink href="https://vc.ru/id808769" target="_blank">
              <IconVC className="w-[34px] cursor-pointer" />
            </NextLink>

            <NextLink
              href={
                translations.footerFooterMenuSectionsSocialLinksTelegramLink
              }
              target="_blank"
            >
              <IconTelegram className="w-[34px] cursor-pointer" />
            </NextLink>
          </div>,
        );
      }

      if (!link.sublinks || link.sublinks.length === 0) {
        return linkElements;
      }

      /** и если есть подгруппа ссылок, то просто дополним элементами */
      return [
        ...linkElements,
        ...link.sublinks.map((sublink, sublinkIndex) => {
          const sublinkName = sublink.name;

          if (!sublinkName) {
            return null;
          }

          return (
            <Typography key={sublinkIndex} variant="accent-m" color="tertiary">
              <Link
                variant="small"
                className="last:mb-5"
                href={getResolvedLink(sublink.href)}
              >
                {sublinkName}
              </Link>
            </Typography>
          );
        }),
      ];
    },
    [getResolvedLink, translations],
  );

  const renderSingleLink = useCallback(
    (link: LinkEntity, linkIndex: number) => {
      const linkName = link.name;

      const linkNameElement = link.href ? (
        /** Заголовок с ссылкой подкатегории */
        <Typography variant="accent-m">
          <Link className="mb-2" href={getResolvedLink(link.href)}>
            <span className="flex items-center gap-1">
              {linkName}
              <IconLink />
            </span>
          </Link>
        </Typography>
      ) : (
        /** Заголовок подкатегории */
        <Typography color="secondary" className="md:pb-3" variant="accent-m">
          {linkName}
        </Typography>
      );

      return (
        <div
          key={linkIndex}
          className="flex w-full max-w-[200px] flex-col gap-2 max-sm:max-w-[300px] max-sm:gap-3"
        >
          {linkName && linkNameElement}
          {renderLinkByType(link)}
        </div>
      );
    },
    [getResolvedLink, renderLinkByType],
  );

  const renderLinksGroup = useCallback(
    (linksEntity: NavigationLinks) => {
      /** Если включен режим multiColumn, рендерим колонки */
      if (linksEntity.multiColumn && linksEntity.columnGroups) {
        return (
          <div className="grid w-full grid-cols-1 gap-x-6 gap-y-9 min-[1168px]:grid-cols-2">
            {linksEntity.columnGroups.map((columnLinks, columnIndex) => (
              <div
                key={columnIndex}
                className="flex flex-col gap-9 max-sm:gap-5"
              >
                {columnLinks.map(renderSingleLink)}
              </div>
            ))}
          </div>
        );
      }

      /** Сформируем группу ссылок (поведение по умолчанию) */
      return linksEntity.links?.map(renderSingleLink);
    },
    [renderSingleLink],
  );

  const renderLinks = useCallback(
    (linksEntity: NavigationLinks, groupIndex: number) => {
      if (!linksEntity.name) {
        return null;
      }

      const groupName = linksEntity.name;

      const handleGroupClick = () => {
        if (!groupName || !isMobile) {
          return;
        }

        openGroup(groupName);
      };

      const isFirstGroup = groupIndex === 0;

      return (
        <div
          key={groupIndex}
          className={classNames({
            cls: 'flex min-w-auto shrink grow flex-col gap-6 max-sm:gap-[6px]',
            mods: {
              'min-w-[340px]': isFirefoxOrSafari && isFirstGroup,
            },
          })}
        >
          <p
            className="flex items-center max-sm:h-10 max-sm:justify-between"
            onClick={handleGroupClick}
          >
            {/** Заголовок категории */}
            <Typography variant="accent-m" as="span">
              {groupName}
            </Typography>

            {/** Расхлопушка категории на мобильной версии */}
            {isMobile && (
              <IconChevron
                className={classNames({
                  mods: {
                    'rotate-180': openGroups.includes(groupName),
                  },
                })}
              />
            )}
          </p>
          <div
            className={classNames({
              cls: 'flex w-full grow flex-col justify-start gap-x-0 gap-y-9 max-sm:pb-[14px]',
              mods: {
                hidden: !openGroups.includes(groupName) && isMobile,
              },
            })}
          >
            {renderLinksGroup(linksEntity)}
          </div>
        </div>
      );
    },
    [isFirefoxOrSafari, isMobile, openGroups, renderLinksGroup],
  );

  const navigationLinks = useMemo(
    () =>
      getNavigationLinks({
        t: translations,
        domain,
        locale,
      }),
    [translations, domain, locale],
  );

  const filterNavigationLinks = useCallback(
    (links: NavigationLinks[]) => {
      return links
        .map((link) => {
          const linkCopy: LinkEntity = { ...link };

          /** Если есть конфигурация для доменов, проверим включение текущего */
          if (link.domains && !link.domains.includes(domain)) {
            return null;
          }

          /** Если есть конфигурация для языков, проверим включение текущего */
          if (link.locales && !link.locales.includes(locale)) {
            return null;
          }

          if (linkCopy.links) {
            /** Сформируем группу ссылок */
            linkCopy.links = filterNavigationLinks(linkCopy.links);
          }

          if (linkCopy.sublinks)
            /** Заполним группу ссылками */
            linkCopy.sublinks = filterNavigationLinks(linkCopy.sublinks);

          if (link.multiColumn && link.columnGroups) {
            /** Фильтруем columnGroups */
            linkCopy.columnGroups = link.columnGroups.map((column) =>
              filterNavigationLinks(column),
            );
          }

          return linkCopy;
        })
        .filter((l) => !!l);
    },
    [domain, locale],
  );

  const links = useMemo(
    () => filterNavigationLinks(navigationLinks).map(renderLinks),
    [navigationLinks, filterNavigationLinks, renderLinks],
  );

  const ruLocale = locale === LOCALES.ru;
  const amLocale = locale === LOCALES.am;

  const hotlinePhone = HOTLINE_PHONE[domain]?.[locale];

  return (
    <footer className="bg-surface-base-secondary w-full">
      <section className="m-auto box-border flex w-full max-w-[1168px] flex-col gap-[50px] px-4 py-9">
        {ruLocale ? (
          <IconLogo className="w-36" />
        ) : (
          <IconLogoEng className="w-36" />
        )}
        <div
          className={classNames({
            cls: 'flex w-full flex-wrap justify-between max-sm:flex-col',
            mods: {
              'gap-x-10': amLocale,
              'gap-x-11': !amLocale,
            },
          })}
          itemScope
          itemType="http://schema.org/SiteNavigationElement"
        >
          <meta itemProp="name" content="Футер сайта" />
          {links}
        </div>
      </section>

      <section className="bg-surface-base-tertiary flex min-h-32 w-full items-center justify-center p-4">
        {isRuHost && isFeatureEnabled('enableMaskedPhone') ? (
          <MaskedPhoneBlock translations={translations} />
        ) : (
          <div className="flex h-full w-[1168px] flex-wrap-reverse items-center justify-between gap-6">
            <span className="text-text-base-secondary">
              {translations.footerFooterMenuCopyright}
            </span>
            <div>
              <p className="text-text-base-secondary grow">
                {translations.footerFooterMenuSectionsContactsTitle}
              </p>
              <div className="mt-3 flex flex-wrap gap-3 max-sm:flex-col">
                {(isKzHost || isRuHost) && hotlinePhone && (
                  <Link variant="small" href={`tel:${hotlinePhone.href}`}>
                    <IconPhone className="mr-1 inline w-6" />
                    {hotlinePhone.label}
                  </Link>
                )}
                <Link variant="small" href="mailto:dev@2gis.ru">
                  <IconMail className="mr-1 inline w-6" />
                  {translations.footerFooterMenuSectionsContactsItemsEmailName}
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>
    </footer>
  );
};
